import {
  DatetimeChangeEventDetail,
  IonDatetime,
  IonPicker,
  IonPickerColumn,
  IonPickerColumnOption,
} from '@ionic/react';
import { IonDatetimeCustomEvent, IonPickerColumnCustomEvent, PickerColumnChangeEventDetail } from '@ionic/core';
import { FC, useEffect, useMemo, useState } from 'react';
import ModalWrapper from '../../components/modals/ModalWrapper';
import moment, { Moment } from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import HeaderEndSlotBtn from '../../components/modals/HeaderEndSlotBtn';
import ModalHeader from '../../components/modals/ModalHeader';
import useOrderSlots from '../../../../hooks/utility/useOrderSlots';
import { IModal } from '../../router/ModalRouter';
import { selectOrderMethodModalData, setScheduledFor } from '../../../../store/slices/OrderMethodSlice';
import { useTranslation } from 'react-i18next';
import { selectOrderMethod } from '../../../../store/slices/OrderSlice';

const DateTimeModal: FC<Partial<IModal>> = ({ goBack }): JSX.Element => {
  const { orderMethod: localOrderMethod, scheduledFor } = useSelector(selectOrderMethodModalData);
  const cachedOrderMethod = useSelector(selectOrderMethod);
  const orderMethod = localOrderMethod ?? cachedOrderMethod;
  const [now] = useState(() => moment());
  const [selectedDate, setSelectedDate] = useState<Moment>(() => {
    if (scheduledFor) {
      return moment(scheduledFor);
    }

    return now;
  });
  const { orderSlots, maxPreOrderDays, isHoliday, isBusinessTime } = useOrderSlots({
    orderMethod,
    startDate: selectedDate.format('YYYY-MM'),
    date: selectedDate,
  });
  const { dayValues, monthValues, max } = useMemo(() => {
    const dayValues = Array.from({ length: maxPreOrderDays }, (_, i) => now.clone().add(i, 'days'))
      .filter((day) => !isHoliday(day.toISOString()))
      .map((day) => +day.format('DD'));
    const monthValues: number[] = [+now.format('MM')];
    const hasNextMonth = now.isAfter(dayValues.at(-1), 'days');
    const max = now.clone().add(maxPreOrderDays, 'days').toISOString();

    hasNextMonth && monthValues.push(+now.clone().add(1, 'month').format('MM'));

    return {
      dayValues,
      monthValues,
      max,
    };
  }, [maxPreOrderDays]);
  const dispatch = useDispatch();
  const [selectedSlot, setSelectedSlot] = useState(() => scheduledFor ?? now.toISOString());
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:DateTime:DateTimeModal' });
  const hasAsap = selectedDate.isSame(now, 'days') && isBusinessTime(now.format());

  useEffect(() => {
    if (hasAsap) {
      return;
    }

    const hasSelectedSlot = orderSlots.some((slot) => selectedDate.isSame(slot.start_time));

    if (hasSelectedSlot) {
      return;
    }

    setSelectedSlot(orderSlots[0]?.start_time);
  }, [orderSlots, selectedDate.format()]);

  const onDateChange = (e: IonDatetimeCustomEvent<DatetimeChangeEventDetail>) => {
    setSelectedDate(moment(e.detail.value));
  };
  const onSlotChange = (e: IonPickerColumnCustomEvent<PickerColumnChangeEventDetail>) => {
    const value = e.detail.value as string;

    setSelectedSlot(value);
  };
  const onDoneClick = () => {
    const isAsap = selectedSlot === now.toISOString();
    const scheduledFor = isAsap ? null : selectedSlot;

    dispatch(setScheduledFor(scheduledFor));
    goBack();
  };

  return (
    <ModalWrapper className="mt-4">
      <ModalHeader
        title={t('title')}
        onBack={goBack}
        endSlot={<HeaderEndSlotBtn onClick={onDoneClick}>{t('buttons.done')}</HeaderEndSlotBtn>}
      />
      <div className="flex">
        <IonDatetime
          isDateEnabled={(date) => !isHoliday(date)}
          onIonChange={onDateChange}
          size="cover"
          min={moment().toISOString()}
          presentation="time-date"
          preferWheel
          value={selectedDate.toISOString()}
          yearValues={[]}
          monthValues={monthValues}
          hourValues={[]}
          minuteValues={[]}
          dayValues={dayValues}
          max={max}
          hour-cycle="h24"
          style={{
            '--background': 'none',
            '--background-rgb': 'none',
          }}
        />
        <IonPicker>
          <IonPickerColumn value={selectedSlot} onIonChange={onSlotChange}>
            <IonPickerColumnOption value="" disabled />
            {hasAsap && <IonPickerColumnOption value={now.toISOString()}>ASAP</IonPickerColumnOption>}
            {orderSlots?.map((slot) => (
              <IonPickerColumnOption key={slot.start_time} value={slot.start_time}>
                {moment(slot.start_time).format('HH:mm')} - {moment(slot.end_time).format('HH:mm')}
              </IonPickerColumnOption>
            ))}
          </IonPickerColumn>
        </IonPicker>
      </div>
    </ModalWrapper>
  );
};

export default DateTimeModal;
